"use client";

import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import {
  Clock,
  Menu,
  Mic,
  Square,
  HelpCircle,
  User,
  FileText,
  Upload,
  ClipboardList,
  Lock,
  Download,
  Copy,
  CheckCircle,
  Activity,
  Calendar,
  AudioLines,
  ChevronDown,
  ChevronUp,
  Pause,
  Play,
  Edit,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { Button } from "../ui/button";
import { TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useTrial } from "../../context/TrialContext";
import { goToTalkToSales } from "../../constants/publicSite";
import VBotSubscriptionModal from "../ui/vbot-subscription-modal";
import { useNavigate } from "react-router-dom";
import { useUserRole } from "../../hooks/useUserRole";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-hot-toast";
import { subscriptionService } from "../../apis/subscriptionService";
import SoapNotePronounBar from "./SoapNotePronounBar";
import {
  resolveChartPronounsForDropdown,
  PRONOUN_DROPDOWN_OPTIONS,
} from "../../utils/inferPronouns";
import RecordingOptions from "./recording-options";
import HeaderTemplateDropdown from "../templates-list";
import { TEMPLATES_FEATURE_ENABLED } from "../../context/FeatureFlagsContext";
import { needsMicCapture } from "../../hooks/useRecording";

const INFO_CARD_BASE =
  "flex items-center gap-2 px-2.5 py-1.5 bg-[#eef1fb] text-slate-700 rounded-xl border border-[#e3e8f7] min-w-0 min-h-[2.5rem]";
const INFO_CARD_LABEL =
  "text-xs uppercase font-semibold tracking-wide text-slate-500 truncate";
const INFO_CARD_VALUE = "text-sm font-bold text-slate-800";
const INFO_CARD_ICON = "text-blue-500 shrink-0";

const CARDS_ROW_MIN_WIDTH = 860;
const CARDS_ICONS_MIN_WIDTH = 1060;
const CARDS_SPLIT_MIN_WIDTH = 560;
const INFO_CARD_COUNT = 7;

const ACTIONS_COMPACT_MAX_WIDTH = 1100;

// Saved date (YYYY-MM-DD or ISO datetime) -> MM/DD/YYYY for the typed visit-date field.
const isoToDisplayDate = (iso) => {
  const value = String(iso || "").trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoMatch) return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
  const displayMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (!displayMatch) return "";
  return `${displayMatch[1].padStart(2, "0")}/${displayMatch[2].padStart(2, "0")}/${displayMatch[3]}`;
};

// Typed MM/DD/YYYY  ->  YYYY-MM-DD, or "" when it isn't a real date.
const displayToIsoDate = (text) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(text || "").trim());
  if (!m) return "";
  const month = Number(m[1]);
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (year < 1900 || year > 2100) return "";
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return "";
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

// Build MM/DD/YYYY while typing, but keep month / day / year as separate
// parts so editing the day does not swallow the year.
// Sequential typing (no slashes yet) still auto-inserts slashes.
// Backspace is treated as a delete so slashes are not immediately re-added.
const formatTypedDate = (raw, previous = "") => {
  const value = String(raw || "").replace(/[^\d/]/g, "").replace(/\/{2,}/g, "/");
  if (!value || value === "/") return "";

  const prevClean = String(previous || "").replace(/[^\d/]/g, "");
  const deleting = value.length < prevClean.length;

  if (!value.includes("/")) {
    const digits = value.slice(0, 8);
    let month;
    let rest;
    if (digits[0] > "1") {
      month = `0${digits[0]}`;
      rest = digits.slice(1);
    } else if (digits.length === 1) {
      return digits;
    } else {
      month = digits.slice(0, 2);
      const monthNumber = Number(month);
      if (monthNumber < 1 || monthNumber > 12) return digits[0];
      rest = digits.slice(2);
    }
    if (!rest) return deleting ? month : `${month}/`;

    let day;
    if (rest[0] > "3") {
      day = `0${rest[0]}`;
      rest = rest.slice(1);
    } else if (rest.length === 1) {
      return `${month}/${rest}`;
    } else {
      day = rest.slice(0, 2);
      rest = rest.slice(2);
    }
    if (!rest) return deleting ? `${month}/${day}` : `${month}/${day}/`;
    return `${month}/${day}/${rest.slice(0, 4)}`;
  }

  const parts = value.split("/");
  const month = (parts[0] || "").slice(0, 2);
  const day = (parts[1] || "").slice(0, 2);
  const year = (parts[2] || "").slice(0, 4);

  if (parts.length === 1) return month;
  if (parts.length === 2) {
    if (!deleting && month.length === 2 && day.length === 2) return `${month}/${day}/`;
    return `${month}/${day}`;
  }
  return `${month}/${day}/${year}`;
};

const getVisitCalendarDays = (monthDate) => {
  const firstDay = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    1,
  ).getDay();
  const calendarStart = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth(),
    1 - firstDay,
  );
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(calendarStart);
    day.setDate(calendarStart.getDate() + index);
    return day;
  });
};

const formatIsoDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const VISIT_CALENDAR_MIN_YEAR = 1900;
const VISIT_DATE_FUTURE_DAYS = 30;

/** YYYY-MM-DD for "today" in America/New_York — matches patient DOS validation. */
const getTodayAmericaISO = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  if (!y || !m || !d) return formatIsoDate(new Date());
  return `${y}-${m}-${d}`;
};

const parseIsoParts = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]) - 1,
    day: Number(match[3]),
  };
};

const shiftIsoByDays = (iso, deltaDays) => {
  const parts = parseIsoParts(iso);
  if (!parts) return "";
  return formatIsoDate(new Date(parts.year, parts.month, parts.day + deltaDays));
};

const getVisitDateMaxIso = () =>
  shiftIsoByDays(getTodayAmericaISO(), VISIT_DATE_FUTURE_DAYS);

const clampCalendarMonth = (date, maxIso) => {
  const max = parseIsoParts(maxIso);
  if (!max) return date;
  const year = date.getFullYear();
  const month = date.getMonth();
  if (year < VISIT_CALENDAR_MIN_YEAR) {
    return new Date(VISIT_CALENDAR_MIN_YEAR, 0, 1);
  }
  if (year > max.year || (year === max.year && month > max.month)) {
    return new Date(max.year, max.month, 1);
  }
  return date;
};

const VISIT_CALENDAR_MONTHS = Array.from({ length: 12 }, (_, month) =>
  new Intl.DateTimeFormat("en", { month: "long" }).format(new Date(2000, month, 1)),
);
const HEADER_TIGHT_MIN_WIDTH = 560;
const HEADER_TIGHT_MAX_WIDTH = 780;
const EDIT_POPUP_MARGIN = 8;
const VISIT_DATE_POPUP_WIDTH = 328;

function clampEditPopupPosition(anchorRect, popupWidth, popupHeight) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = EDIT_POPUP_MARGIN;
  const width = Math.min(popupWidth, Math.max(0, vw - margin * 2));
  const height = Math.min(popupHeight, Math.max(0, vh - margin * 2));

  let left = anchorRect.left;
  if (vw <= width + margin * 2 + 4) {
    left = (vw - width) / 2;
  } else {
    if (left + width > vw - margin) left = vw - margin - width;
    if (left < margin) left = margin;
  }

  const spaceBelow = vh - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const placeAbove = height > spaceBelow && spaceAbove > spaceBelow;
  let top = placeAbove ? anchorRect.top - height - 6 : anchorRect.bottom + 6;
  if (top + height > vh - margin) top = vh - margin - height;
  if (top < margin) top = margin;

  return { top, left, width };
}

function getInfoCardsLayout(width) {
  if (width >= CARDS_ROW_MIN_WIDTH) return "row";
  if (width >= CARDS_SPLIT_MIN_WIDTH) return "split";
  return "compact";
}

const INFO_CARDS_GRID_COLUMNS = {
  row: `repeat(${INFO_CARD_COUNT}, minmax(0, 1fr))`,
  split: "repeat(12, minmax(0, 1fr))",
  compact: "repeat(2, minmax(0, 1fr))",
};

/** Grid placement for card `index` (0-6) so rows are always evenly filled. */
function getInfoCardStyle(layout, index) {
  if (layout === "split")
    return { gridColumn: index < 4 ? "span 3" : "span 4" };
  if (layout === "compact" && index === INFO_CARD_COUNT - 1) {
    return { gridColumn: "1 / -1" };
  }
  return undefined;
}

const VISIT_TYPE_HEADER_LABELS = {
  awv_ap_360: "AWV/AP/360",
  welcome_to_medicare: "Welcome to Medicare",
  regular_follow_up: "Follow-Up",
  ad_hoc_follow_up: "Ad-hoc Follow-Up",
  new_patient: "New Patient",
  tcm_visit_post_hospitalization: "TCM",
  sick_visit: "Sick Visit",
};
const VISIT_TYPE_ACRONYMS = new Set(["awv", "ap", "tcm", "wtm"]);

const formatVisitTypeLabel = (visitType) => {
  if (!visitType) return "--";
  const key = String(visitType).trim().toLowerCase();
  if (VISIT_TYPE_HEADER_LABELS[key]) return VISIT_TYPE_HEADER_LABELS[key];
  return key
    .split("_")
    .filter(Boolean)
    .map((word) =>
      VISIT_TYPE_ACRONYMS.has(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

export default function SessionHeader({
  isRecording,
  isPaused = false,
  isParked = false,
  isResumingCapture = false,
  isProcessing = false,
  /** Visits are still loading/switching — keep the Upload button disabled. */
  isSessionLoading = false,
  onPauseResumeRecording,
  /** "azure" | "browser" | null — from useRecording; drives tab label, not .env. */
  transcriptionSource = null,
  /** True while an async file upload job is streaming transcript (WebSocket). */
  isUploadTranscriptionStreaming = false,
  onToggleRecording,
  recordingTime,
  onClearSession,
  activeSessionName,
  mobileMenuOpen,
  mobileSection,
  setMobileMenuOpen,
  patientContext = {},
  isPatientDetailsFilled,
  soapNote,
  activeTab,
  handleTabChange,
  aiService,
  onUpdateSoapNote,
  isUpdatingPronouns = false,
  isAssignedSOAPNote = false,
  onStartProcessing,
  onEndProcessing,
  onUpdatePatient,
  onShowTranscriptionInSoapTab,

  // ----- Per-chart HPI depth (1=Concise, 2=Balanced, 3=Detailed) -----
  hpiLevel = 3,
  onHpiLevelChange,
  /** Toggle HPI depth AFTER generation (regenerates/caches the HPI per level). */
  onRegenerateHpi,
  hpiRegenerating = false,

  // ----- NEW props for recording options -----
  audioSourceMode = "input",
  onAudioSourceModeChange,
  audioInputDeviceId = "",
  onAudioInputDeviceChange,
  getAudioInputDevices,
  /** Called when Capture options open — pre-request mic before screen share on Capture click. */
  onPreRequestMicPermission,
  /** Warm live STT WebSocket while Capture options are open. */
  onWarmLiveWebSocket,
  onRestartTutorial,
  tourTemplateOpen = false,
  onTourTemplateOpenChange,
  tourRecordingOpen = false,
  onTourRecordingOpenChange,
  isTourCaptureStep = false,
}) {
  const {
    isAiFeatureLocked,
    isProPlusLocked,
    getAccountLimitMessage,
    trialStatus,
  } = useTrial();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [emrRequesting, setEmrRequesting] = useState(false);
  const helpDropdownRef = useRef(null);
  const [showMobilePatientDetails, setShowMobilePatientDetails] =
    useState(false);
  const [showPronounCard, setShowPronounCard] = useState(false);
  const pronounButtonRef = useRef(null);
  const pronounCardRef = useRef(null);
  const [pronounPosition, setPronounPosition] = useState({ top: 0, left: 0 });
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // ----- State for recording options popup -----
  const [showRecordingPopup, setShowRecordingPopup] = useState(false);
  const recordingPopupRef = useRef(null);
  const chevronButtonRef = useRef(null);
  const micPreRequestInFlightRef = useRef(false);

  useEffect(() => {
    setShowRecordingPopup(tourRecordingOpen);
  }, [tourRecordingOpen]);

  // Keep the options popup fully inside the viewport. It is anchored under the
  // Capture group but clamped to the screen edges, so on phones (where the
  // group can wrap to the middle of the row) it never spills off the left side.
  const [recordingPopupPos, setRecordingPopupPos] = useState(null);
  useEffect(() => {
    if (!showRecordingPopup) {
      setRecordingPopupPos(null);
      return undefined;
    }
    const PAD = 8;
    const update = () => {
      const anchor =
        chevronButtonRef.current?.closest(
          "[data-tour='recording-tour-area']",
        ) || chevronButtonRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - PAD * 2);
      const left = Math.max(
        PAD,
        Math.min(rect.right - width, window.innerWidth - width - PAD),
      );
      const top = rect.bottom + 4;
      setRecordingPopupPos({
        top,
        left,
        width,
        maxHeight: Math.max(160, window.innerHeight - top - PAD),
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [showRecordingPopup]);

  // Pre-request mic when Capture options open so screen share is not wasted on a later denial.
  // Also warm the live-transcription WebSocket during options so status is ready on Start.
  useEffect(() => {
    if (!showRecordingPopup || isRecording || soapNote || isAiFeatureLocked) {
      return;
    }

    if (typeof onWarmLiveWebSocket === "function") {
      void onWarmLiveWebSocket();
    }

    if (!onPreRequestMicPermission || !needsMicCapture(audioSourceMode)) {
      return;
    }

    if (micPreRequestInFlightRef.current) return;
    micPreRequestInFlightRef.current = true;

    void onPreRequestMicPermission(audioInputDeviceId || undefined).finally(
      () => {
        micPreRequestInFlightRef.current = false;
      },
    );
  }, [
    showRecordingPopup,
    audioSourceMode,
    audioInputDeviceId,
    isRecording,
    soapNote,
    isAiFeatureLocked,
    onPreRequestMicPermission,
    onWarmLiveWebSocket,
  ]);

  // ----- Role hooks (unchanged) -----
  const {
    role: userRole,
    isAdmin,
    isFullTimeScribe,
    isPartTimeScribe,
    isScriber,
    canCreateSessions,
    canEditSessions,
    canDeleteSessions,
  } = useUserRole();

  // Admin can access all tabs on every chart; others: all tabs only when not part-time scribe and not assigned SOAP note
  const canAccessAllTabs =
    isAdmin || (!isPartTimeScribe && !isAssignedSOAPNote);
  // Determine if user can edit patient info (reuse role logic – same as PatientContextForm)
  const canEditPatientInfo = !isPartTimeScribe && !isAssignedSOAPNote;

  // --- New state for inline editing of Age / Gender ---
  const [editingField, setEditingField] = useState(null);
  const [tempAge, setTempAge] = useState("");
  const [tempGender, setTempGender] = useState("");
  const [tempVisitDate, setTempVisitDate] = useState("");
  const [tempVisitDateText, setTempVisitDateText] = useState("");
  const [visitCalendarMonth, setVisitCalendarMonth] = useState(() => new Date());
  const [showVisitYearList, setShowVisitYearList] = useState(false);
  const [visitYearListPlacement, setVisitYearListPlacement] = useState({
    openUp: false,
    maxHeight: 240,
  });
  const visitYearListRef = useRef(null);
  const visitYearButtonRef = useRef(null);
  const selectedVisitYearRef = useRef(null);
  useEffect(() => {
    const list = visitYearListRef.current;
    const selectedYear = selectedVisitYearRef.current;
    if (showVisitYearList && list && selectedYear) {
      list.scrollTop = Math.max(
        0,
        selectedYear.offsetTop - list.clientHeight / 2,
      );
    }
  }, [showVisitYearList, visitCalendarMonth]);
  const ageEditRef = useRef(null);
  const genderEditRef = useRef(null);
  const visitDateEditRef = useRef(null);

  const headerRootRef = useRef(null);
  const [headerWidth, setHeaderWidth] = useState(ACTIONS_COMPACT_MAX_WIDTH);
  useLayoutEffect(() => {
    const el = headerRootRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setHeaderWidth(w);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const compactActions = headerWidth < ACTIONS_COMPACT_MAX_WIDTH;
  const tightHeader =
    headerWidth >= HEADER_TIGHT_MIN_WIDTH &&
    headerWidth < HEADER_TIGHT_MAX_WIDTH;

  // Measure the cards container so the layout adapts to the real available width.
  const infoCardsRef = useRef(null);
  const [infoCardsWidth, setInfoCardsWidth] = useState(CARDS_ICONS_MIN_WIDTH);
  useLayoutEffect(() => {
    const el = infoCardsRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setInfoCardsWidth(w);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isPatientDetailsFilled]);
  const infoCardsLayout = getInfoCardsLayout(infoCardsWidth);
  const cardIconClass =
    infoCardsLayout === "row" && infoCardsWidth < CARDS_ICONS_MIN_WIDTH
      ? "hidden"
      : INFO_CARD_ICON;

  // --- Handlers for inline editing ---
  const [editPopupPos, setEditPopupPos] = useState(null);
  const visitDateCardRef = useRef(null);
  const ageCardRef = useRef(null);
  const genderCardRef = useRef(null);

  const openEditPopup = (cardRef, popupHeight = 300, popupWidth = 300) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) {
      const width = Math.min(popupWidth, window.innerWidth - EDIT_POPUP_MARGIN * 2);
      setEditPopupPos({ top: 120, left: EDIT_POPUP_MARGIN, width });
      return;
    }
    setEditPopupPos(clampEditPopupPosition(rect, popupWidth, popupHeight));
  };

  const startEditAge = () => {
    if (!canEditPatientInfo) return;
    setTempAge(patientContext.age?.toString() || "");
    openEditPopup(ageCardRef);
    setEditingField("age");
  };

  const startEditGender = () => {
    if (!canEditPatientInfo) return;
    setTempGender(patientContext.gender || "");
    openEditPopup(genderCardRef);
    setEditingField("gender");
  };

  const visitDateTodayIso = getTodayAmericaISO();
  const visitDateMaxIso = getVisitDateMaxIso();
  const visitDateMaxParts = parseIsoParts(visitDateMaxIso);
  const visitDateEditYears = visitDateMaxParts
    ? Array.from(
        { length: visitDateMaxParts.year - VISIT_CALENDAR_MIN_YEAR + 1 },
        (_, index) => VISIT_CALENDAR_MIN_YEAR + index,
      )
    : [new Date().getFullYear()];

  const startEditVisitDate = () => {
    if (!canEditPatientInfo) return;
    const rawVisitDate = patientContext.dateOfService || "";
    const visitDate =
      /^(\d{4}-\d{2}-\d{2})/.exec(rawVisitDate)?.[1] ||
      displayToIsoDate(rawVisitDate) ||
      "";
    const maxIso = getVisitDateMaxIso();
    setTempVisitDate(visitDate && visitDate <= maxIso ? visitDate : "");
    // Use an empty MM/DD/YYYY field so a new date can be typed immediately,
    // instead of showing the saved value.
    setTempVisitDateText("");
    setShowVisitYearList(false);
    const selectedDate =
      visitDate && visitDate <= maxIso
        ? new Date(`${visitDate}T00:00:00`)
        : new Date(`${maxIso}T00:00:00`);
    setVisitCalendarMonth(
      clampCalendarMonth(
        Number.isNaN(selectedDate.getTime()) ? new Date() : selectedDate,
        maxIso,
      ),
    );
    openEditPopup(visitDateCardRef, 420, VISIT_DATE_POPUP_WIDTH);
    setEditingField("visitDate");
  };

  const cancelEdit = () => {
    setEditingField(null);
    setTempAge("");
    setTempGender("");
    setTempVisitDate("");
    setTempVisitDateText("");
  };

  const saveAge = () => {
    if (!canEditPatientInfo) return;
    const newAge = parseInt(tempAge, 10);
    if (!isNaN(newAge) && newAge > 0 && newAge <= 99) {
      onUpdatePatient?.({ age: newAge });
    } else if (tempAge === "") {
      // Allow clearing age – set to null/undefined
      onUpdatePatient?.({ age: null });
    } else {
      toast.error("Please enter a valid age (positive number)");
      return;
    }
    cancelEdit();
  };

  // Selecting a gender saves it and closes the popover straight away.
  const saveGender = (value = tempGender) => {
    if (!canEditPatientInfo) return;
    const newGender = String(value || "").trim();
    // Never clear an existing gender from here – keep the previous value if nothing is chosen
    if (newGender && newGender !== (patientContext.gender || "")) {
      onUpdatePatient?.({ gender: newGender });
    }
    cancelEdit();
  };

  // Visit date: type it (MM/DD/YYYY) or pick it from the calendar.
  // Selecting a day (or Today) saves immediately. Enter also saves a typed date.
  // `silent` = closing by clicking outside, so don't show an error for half-typed text.
  const saveVisitDate = (isoOverride, silent = false) => {
    if (!canEditPatientInfo) return;
    const iso = typeof isoOverride === "string" ? isoOverride : displayToIsoDate(tempVisitDateText);
    if (!iso) {
      if (!silent && tempVisitDateText.trim()) {
        toast.error("Please enter a valid date (MM/DD/YYYY)");
        return;
      }
      // Nothing (valid) entered – keep the previous visit date
      cancelEdit();
      return;
    }
    const maxIso = getVisitDateMaxIso();
    if (iso > maxIso) {
      if (!silent) {
        toast.error("Date of Service cannot be more than 30 days in the future");
      }
      if (silent) cancelEdit();
      return;
    }
    if (iso !== (patientContext.dateOfService || "")) {
      onUpdatePatient?.({ dateOfService: iso });
    }
    cancelEdit();
  };

  const selectVisitDate = (iso) => {
    if (!iso) return;
    saveVisitDate(iso);
  };

  // ----- Close popups when clicking outside -----
  // Ignore Radix Select portal clicks — the mic dropdown renders on document.body,
  // so treating it as "outside" would close this popup before a device can be chosen.
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (
        target?.closest?.(
          "[data-radix-select-content], [data-radix-popper-content-wrapper], [role='listbox']",
        )
      ) {
        return;
      }
      if (
        showRecordingPopup &&
        !tourRecordingOpen &&
        recordingPopupRef.current &&
        !recordingPopupRef.current.contains(target) &&
        chevronButtonRef.current &&
        !chevronButtonRef.current.contains(target)
      ) {
        setShowRecordingPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRecordingPopup, tourRecordingOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        helpDropdownRef.current &&
        !helpDropdownRef.current.contains(event.target)
      ) {
        setShowHelpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showPronounCard &&
        pronounButtonRef.current &&
        !pronounButtonRef.current.contains(event.target) &&
        pronounCardRef.current &&
        !pronounCardRef.current.contains(event.target)
      ) {
        setShowPronounCard(false);
      }
    };
    const handleResize = () => setShowPronounCard(false);
    document.addEventListener("mousedown", handleClickOutside);
    if (showPronounCard) window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
    };
  }, [showPronounCard]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        editingField === "age" &&
        ageEditRef.current &&
        !ageEditRef.current.contains(event.target)
      ) {
        saveAge();
      }
      if (
        editingField === "gender" &&
        genderEditRef.current &&
        !genderEditRef.current.contains(event.target)
      ) {
        saveGender();
      }
      if (
        editingField === "visitDate" &&
        visitDateEditRef.current &&
        !visitDateEditRef.current.contains(event.target)
      ) {
        saveVisitDate(undefined, true);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editingField, tempAge, tempGender, tempVisitDate, tempVisitDateText]);

  useLayoutEffect(() => {
    if (!editingField) return undefined;
    const popupRef =
      editingField === "age"
        ? ageEditRef
        : editingField === "gender"
          ? genderEditRef
          : visitDateEditRef;
    const cardRef =
      editingField === "age"
        ? ageCardRef
        : editingField === "gender"
          ? genderCardRef
          : visitDateCardRef;

    const reposition = () => {
      const popup = popupRef.current;
      const card = cardRef.current;
      if (!popup || !card) return;
      const cardRect = card.getBoundingClientRect();
      const popupWidth =
        editingField === "visitDate"
          ? VISIT_DATE_POPUP_WIDTH
          : popup.offsetWidth;
      const next = clampEditPopupPosition(
        cardRect,
        popupWidth,
        popup.scrollHeight,
      );
      setEditPopupPos((prev) => {
        if (
          prev &&
          Math.abs(prev.top - next.top) < 0.5 &&
          Math.abs(prev.left - next.left) < 0.5 &&
          Math.abs((prev.width || 0) - (next.width || 0)) < 0.5
        ) {
          return prev;
        }
        return next;
      });

      if (showVisitYearList && visitYearButtonRef.current) {
        const yearRect = visitYearButtonRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - yearRect.bottom - 12;
        const spaceAbove = yearRect.top - 12;
        const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
        const maxHeight = Math.max(
          120,
          Math.min(296, openUp ? spaceAbove : spaceBelow),
        );
        setVisitYearListPlacement((prev) =>
          prev.openUp === openUp && prev.maxHeight === maxHeight
            ? prev
            : { openUp, maxHeight },
        );
      }
    };

    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("orientationchange", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("orientationchange", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [editingField, showVisitYearList]);

  // ----- Helpers (unchanged) -----
  const formatDate = (dateString) => {
    if (!dateString) return "----";
    return isoToDisplayDate(dateString) || "----";
  };

  const today = new Date().toLocaleDateString();
  const generatedDate = useMemo(() => {
    if (!soapNote) return null;
    // Try common fields where generation date might be stored
    const date =
      soapNote.metadata?.generatedAt ||
      soapNote.createdAt ||
      soapNote.generatedAt;
    return date ? new Date(date).toLocaleDateString() : null;
  }, [soapNote]);

  // --- Determine current pronoun label for the header ---
  const currentPronounInfo = useMemo(() => {
    if (!soapNote && !patientContext) return null;
    const resolved = resolveChartPronounsForDropdown(soapNote, patientContext);
    const option = PRONOUN_DROPDOWN_OPTIONS.find(
      (opt) => opt.value === resolved.pronouns,
    );
    return option ? option.label : null;
  }, [soapNote, patientContext, soapNote?.metadata?.pronouns]);

  // --- Helper Functions for PDF/Copy ---

  const formatContent = (content) => {
    if (!content) return "No content available";
    return content
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/- \*\*(.*?)\*\*/g, "• $1")
      .replace(/^\s*-\s*/gm, "• ")
      .replace(/\n\s*\n/g, "\n\n")
      .trim();
  };

  const stripSignatureFromPlan = (content) =>
    String(content || "")
      .replace(/\n?\s*[-*•]?\s*Signature\s*:?\s*[\s\S]*$/i, "")
      .trim();

  const handleCopy = () => {
    if (!soapNote) return;
    const subjective =
      soapNote.subjective || soapNote.structuredSections?.subjective || "";
    const objective =
      soapNote.objective || soapNote.structuredSections?.objective || "";
    const assessment =
      soapNote.assessment || soapNote.structuredSections?.assessment || "";
    const plan = soapNote.plan || soapNote.structuredSections?.plan || "";
    const formattedContent = `SUBJECTIVE:
${formatContent(subjective)}

OBJECTIVE:
${formatContent(objective)}

ASSESSMENT:
${formatContent(assessment)}

PLAN:
${stripSignatureFromPlan(formatContent(plan))}`;
    navigator.clipboard
      .writeText(formattedContent)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Copy failed:", err));
  };

  const handleExportPDF = () => {
    if (!soapNote) return;
    setIsGeneratingPdf(true);
    const subjective =
      soapNote.subjective || soapNote.structuredSections?.subjective || "";
    const objective =
      soapNote.objective || soapNote.structuredSections?.objective || "";
    const assessment =
      soapNote.assessment || soapNote.structuredSections?.assessment || "";
    const plan = soapNote.plan || soapNote.structuredSections?.plan || "";
    try {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        setIsGeneratingPdf(false);
        toast.error("Popup blocked. Please allow popups.");
        return;
      }
      const pdfContent = `<!DOCTYPE html>
<html>
<head><title>SOAP Note - ${patientContext.name || "Patient"}</title>
<style>
body { font-family: sans-serif; line-height: 1.6; padding: 20px; }
h1 { color: #2c3e50; border-bottom: 2px solid #eee; padding-bottom: 10px; }
h2 { color: #34495e; margin-top: 20px; border-bottom: 1px solid #eee; }
.header { margin-bottom: 30px; background: #f8f9fa; padding: 15px; border-radius: 5px; }
.meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 0.9em; }
.section { margin-bottom: 20px; }
.content { white-space: pre-wrap; }
</style>
</head>
<body>
<h1>Medical SOAP Note</h1>
<div class="header"><div class="meta">
<div><strong>Patient:</strong> ${patientContext.name || "Unknown"}</div>
<div><strong>Date:</strong> ${generatedDate || today}</div>
<div><strong>Age/Gender:</strong> ${patientContext.age || "--"} / ${patientContext.gender || "--"}</div>
</div></div>
<div class="section"><h2>Subjective</h2><div class="content">${formatContent(subjective)}</div></div>
<div class="section"><h2>Objective</h2><div class="content">${formatContent(objective)}</div></div>
<div class="section"><h2>Assessment</h2><div class="content">${formatContent(assessment)}</div></div>
<div class="section"><h2>Plan</h2><div class="content">${formatContent(plan)}</div></div>
</body>
</html>`;
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        setIsGeneratingPdf(false);
      }, 250);
    } catch (error) {
      console.error("PDF generation failed:", error);
      setIsGeneratingPdf(false);
    }
  };

  const emrInputEligible =
    trialStatus?.emrInputEligible ??
    trialStatus?.accountLimits?.emrInputEligible ??
    false;

  const handleRequestEmrInput = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId =
      params.get("sessionId") ||
      soapNote?.sessionId ||
      soapNote?.session_id ||
      null;
    if (!sessionId) {
      toast.error("Open a chart session to request EMR input");
      return;
    }
    setEmrRequesting(true);
    try {
      const data = await subscriptionService.requestSessionEmrInput(sessionId);
      const price =
        data?.priceLabel ||
        (data?.charge?.unitAmountCents != null
          ? `$${(data.charge.unitAmountCents / 100).toFixed(2)}`
          : "$1.99");
      toast.success(
        data?.alreadyRequested
          ? "EMR input already requested for this chart"
          : `EMR input requested (${price}/chart)`,
      );
    } catch (err) {
      const nested =
        err?.response?.data?.body?.data || err?.response?.data?.data || {};
      const code = nested.errorCode;
      const msg =
        err?.response?.data?.body?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not request EMR input";
      if (code === "EMR_NOT_ENABLED") {
        toast.error("Enable optional EMR input in Billing settings first");
        navigate("/scribe/settings?tab=billing");
      } else {
        toast.error(msg);
      }
    } finally {
      setEmrRequesting(false);
    }
  };

  return (
    <div
      ref={headerRootRef}
      data-session-header
      className="sticky top-0 z-40 shrink-0 bg-white border-b border-gray-200 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-100 gap-x-3 gap-y-2">
        {/* Tabs */}
        <div className="w-full sm:w-auto overflow-visible">
          <TabsList className="flex flex-nowrap items-center justify-center sm:justify-start gap-1 bg-white border border-slate-200 shadow-sm rounded-full p-1 h-auto w-full sm:w-auto">
            {/* Patient Info tab - hide for part-time scribes and assigned notes */}
            {/* {!isAssignedSOAPNote && canAccessAllTabs && ( */}
            <TabsTrigger
              value="patient"
              data-tour="patient-tab"
              className={`relative flex flex-1 sm:flex-none items-center justify-center gap-1.5 sm:gap-2 ${compactActions ? "px-3" : "px-3 sm:px-4 min-[1700px]:px-5"} py-2 sm:py-2.5 ${tightHeader ? "text-sm" : "text-sm sm:text-base"} font-medium transition-all duration-150 rounded-full
              data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600
              data-[state=active]:text-white data-[state=active]:shadow-md
              data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-800
              data-[state=inactive]:hover:bg-slate-50
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
            >
              {!tightHeader && (
                <>
                  <User size={16} className="sm:hidden" />
                  <User size={16} className="hidden sm:block" />
                </>
              )}
              <span className="whitespace-nowrap">Patient Info</span>
            </TabsTrigger>
            {/* SOAP Note tab - ALWAYS visible for all users */}
            <TabsTrigger
              value="soap-note"
              data-tour="soap-tab"
              className={`relative flex flex-1 sm:flex-none items-center justify-center gap-1.5 sm:gap-2 ${compactActions ? "px-3" : "px-3 sm:px-4 min-[1700px]:px-5"} py-2 sm:py-2.5 ${tightHeader ? "text-sm" : "text-sm sm:text-base"} font-medium transition-all duration-150 rounded-full
              data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-teal-600
              data-[state=active]:text-white data-[state=active]:shadow-md
              data-[state=inactive]:text-slate-500 data-[state=inactive]:hover:text-slate-800
              data-[state=inactive]:hover:bg-slate-50
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
            >
              {!tightHeader && (
                <>
                  <ClipboardList size={16} className="sm:hidden" />
                  <ClipboardList size={16} className="hidden sm:block" />
                </>
              )}
              <span className="whitespace-nowrap">SOAP Note</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Actions - Hide for assigned SOAP notes */}
        {!isAssignedSOAPNote && (
          <div className="flex items-center justify-center sm:justify-end gap-2 sm:ml-auto flex-wrap w-full sm:w-auto min-w-0">
            {/* Recording Controls - Only show if user can access all tabs */}
            {canAccessAllTabs && (
              <div className="flex flex-wrap sm:flex-nowrap items-center justify-center gap-2 font-medium">
                {/* Templates Dropdown */}
                {TEMPLATES_FEATURE_ENABLED && (
                <div data-tour="templates-dropdown">
                  <HeaderTemplateDropdown
                    tourOpen={tourTemplateOpen}
                    onTourOpenChange={onTourTemplateOpenChange}
                    compact={compactActions}
                    tight={tightHeader}
                  />
                </div>
                )}
                {/* Upload button */}
                <button
                  data-tour="upload-tab"
                  onClick={() => {
                    if (isSessionLoading) return;
                    if (soapNote || isAiFeatureLocked) {
                      if (isAiFeatureLocked)
                        toast.error(getAccountLimitMessage());
                      return;
                    }
                    if (!isPatientDetailsFilled) {
                      toast.error(
                        "Please fill in patient details first before starting transcription",
                      );
                      return;
                    }
                    window.dispatchEvent(
                      new CustomEvent("vbot-open-upload-file-picker"),
                    );
                  }}
                  disabled={
                    Boolean(soapNote) || isAiFeatureLocked || isSessionLoading
                  }
                  title={
                    isSessionLoading
                      ? "Loading visit… upload will be available in a moment"
                      : "Upload audio"
                  }
                  aria-label="Upload audio"
                  className={`flex items-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 min-h-9 sm:min-h-10 text-sm font-medium rounded-full border border-slate-200 bg-white shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    compactActions ? "px-2.5" : "px-2.5 sm:px-4"
                  }${
                    isAiFeatureLocked
                      ? " text-gray-400"
                      : " text-slate-700 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50/50"
                  }`}
                >
                  {isAiFeatureLocked && <Lock size={14} />}
                  <Upload size={16} />
                  <span>Upload</span>
                </button>

                {/* Capture button group */}
                <div
                  className="relative inline-block"
                  data-tour="recording-tour-area"
                >
                  <div
                    className={`flex overflow-hidden rounded-full shadow-md ${
                      isTourCaptureStep && !isRecording
                        ? "ring-2 ring-blue-500 shadow-lg"
                        : ""
                    }`}
                    data-tour="recording-controls"
                  >
                    {isRecording && onPauseResumeRecording && (
                      <button
                        type="button"
                        onClick={onPauseResumeRecording}
                        disabled={isResumingCapture || isProcessing}
                        className={`flex items-center gap-1.5 px-2.5 sm:px-3.5 h-9 sm:h-10 text-sm font-medium rounded-l-full border-r border-white/20 disabled:opacity-70 disabled:cursor-wait
        ${isParked || isPaused ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}
                        title={
                          isResumingCapture
                            ? "Resuming capture…"
                            : isParked
                              ? "Resume this visit's saved capture"
                              : isPaused
                                ? "Resume capture"
                                : "Pause capture — you can switch visits and come back"
                        }
                      >
                        {isResumingCapture ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            <span className="hidden sm:inline">Resuming…</span>
                          </>
                        ) : isParked || isPaused ? (
                          <>
                            <Play size={14} className="fill-white" />
                            <span className="hidden sm:inline">
                              {isParked ? "Resume visit" : "Resume"}
                            </span>
                          </>
                        ) : (
                          <>
                            <Pause size={14} />
                            <span className="hidden sm:inline">Pause</span>
                          </>
                        )}
                      </button>
                    )}
                    <button
                      disabled={
                        isTourCaptureStep
                          ? isProcessing
                          : isProcessing ||
                            (!isRecording &&
                              (Boolean(soapNote) || isAiFeatureLocked))
                      }
                      onClick={() => {
                        if (!isRecording && isAiFeatureLocked && !isTourCaptureStep) {
                          toast.error(getAccountLimitMessage());
                          return;
                        }
                        onToggleRecording?.();
                      }}
                      className={`flex items-center gap-1.5 sm:gap-2 ${compactActions ? "px-3.5" : "px-3 sm:px-5"} h-9 sm:h-10 text-sm font-medium transition-colors duration-200 ${
                        isTourCaptureStep
                          ? ""
                          : "disabled:opacity-50 disabled:cursor-not-allowed"
                      }
        ${isRecording && onPauseResumeRecording ? "" : "rounded-l-full"}
        ${
          isRecording
            ? "bg-red-600 text-white hover:bg-red-700"
            : isAiFeatureLocked && !isTourCaptureStep
              ? "bg-gray-400 text-white"
              : "bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-500"
        }`}
                    >
                      {isRecording ? (
                        <>
                          <Square size={14} className="fill-white" />
                          {isPaused ? "Stop" : "Stop"}
                        </>
                      ) : (
                        <>
                          {isAiFeatureLocked && (
                            <Lock size={14} className="shrink-0" />
                          )}
                          <AudioLines size={16} />
                          Capture
                        </>
                      )}
                    </button>

                    {/* Chevron button */}
                    <button
                      ref={chevronButtonRef}
                      data-tour="recording-options-chevron"
                      onClick={() => {
                        const next = !showRecordingPopup;
                        setShowRecordingPopup(next);
                        onTourRecordingOpenChange?.(next);
                      }}
                      disabled={isRecording || isAiFeatureLocked}
                      className={`
        flex items-center justify-center w-9 sm:w-10 h-9 sm:h-10 rounded-r-full border-l border-white/20
        disabled:opacity-50 disabled:cursor-not-allowed
        ${
          isRecording || soapNote || isAiFeatureLocked
            ? "bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-500"
            : "bg-gradient-to-r from-blue-600 to-teal-600 text-white hover:from-blue-700 hover:to-teal-500"
        }
      `}
                    >
                      {showRecordingPopup ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </button>
                  </div>

                  {/* Recording Options Popup – now inside the relative wrapper */}
                  {showRecordingPopup && (
                    <div
                      ref={recordingPopupRef}
                      data-tour="recording-options"
                      className={`z-[10001] bg-white rounded-2xl shadow-xl border border-blue-300 ring-2 ring-blue-100 p-4 overflow-y-auto ${
                        recordingPopupPos
                          ? "fixed"
                          : "absolute right-0 top-full mt-1 w-[calc(100vw-1rem)] sm:w-80 max-w-[20rem]"
                      }`}
                      style={recordingPopupPos || undefined}
                    >
                      <RecordingOptions
                        audioSourceMode={audioSourceMode}
                        onAudioSourceModeChange={onAudioSourceModeChange}
                        audioInputDeviceId={audioInputDeviceId}
                        onAudioInputDeviceChange={onAudioInputDeviceChange}
                        getAudioInputDevices={getAudioInputDevices}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Export & Copy Buttons - visible only when SOAP note exists */}
            {soapNote && (
              <>
                <div
                  className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-center sm:justify-end whitespace-nowrap"
                  data-tour="export-actions"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportPDF}
                    disabled={isGeneratingPdf}
                    title="Export PDF"
                    aria-label="Export PDF"
                    className={`h-9 sm:h-10 text-sm rounded-full border-blue-200 text-blue-700 bg-white hover:bg-blue-50 shadow-sm ${
                      compactActions ? "px-2.5" : "px-2.5 sm:px-4"
                    }`}
                  >
                    {isGeneratingPdf ? (
                      <span
                        className={`animate-spin ${compactActions ? "" : "mr-1"}`}
                      >
                        ⏳
                      </span>
                    ) : (
                      <Download
                        size={16}
                        className={compactActions ? "" : "mr-1"}
                      />
                    )}
                    {!compactActions && "Export PDF"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    title={copied ? "Copied" : "Copy"}
                    aria-label="Copy SOAP note"
                    className={`h-9 sm:h-10 text-sm rounded-full border-blue-200 text-blue-700 bg-white hover:bg-blue-50 shadow-sm ${
                      compactActions ? "px-2.5" : "px-2.5 sm:px-4"
                    }`}
                  >
                    {copied ? (
                      <CheckCircle
                        size={16}
                        className={`${compactActions ? "" : "mr-1"} text-green-500`}
                      />
                    ) : (
                      <Copy
                        size={16}
                        className={compactActions ? "" : "mr-1"}
                      />
                    )}
                    {!compactActions && (copied ? "Copied" : "Copy")}
                  </Button>
                  {/* EMR input button — hidden for now
                  {emrInputEligible && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRequestEmrInput}
                      disabled={emrRequesting}
                      className="h-9 sm:h-10 px-2.5 sm:px-4 text-sm rounded-full border-blue-200 text-blue-700 bg-white hover:bg-blue-50 shadow-sm"
                      title={
                        emrInputEligible
                          ? `Optional EMR chart entry (${
                              trialStatus?.emrInputPriceCents != null
                                ? `$${(trialStatus.emrInputPriceCents / 100).toFixed(2)}`
                                : trialStatus?.planKey === "elite"
                                  ? "$1.29"
                                  : "$1.99"
                            }/chart${
                              trialStatus?.emrTurnaround === "same_day"
                                ? ", same-day"
                                : ""
                            })`
                          : undefined
                      }
                    >
                      <Upload size={12} className="mr-1" />
                      {emrRequesting ? "Requesting…" : "EMR input"}
                    </Button>
                  )}
                  */}
                </div>
              </>
            )}

            {/* Help dropdown */}
            {/* <div className="relative" ref={helpDropdownRef}>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 rounded-full border border-slate-200 bg-white hover:bg-slate-50 shadow-sm"
                onClick={() => setShowHelpDropdown(!showHelpDropdown)}
              >
                <HelpCircle className="w-4 h-4 text-gray-500" />
              </Button>
              {showHelpDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-lg border border-gray-200 py-2 z-50">
                  <button
                    className="w-full px-4 py-2 text-left text-base text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowHelpDropdown(false);
                      navigate("/support-ticket");
                    }}
                  >
                    Raise a Ticket
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-base text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowHelpDropdown(false);
                      window.open("https://virtualscribe.ai/help-center", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Help Center
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    className="w-full px-4 py-2 text-left text-base text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowHelpDropdown(false);
                      window.open("https://virtualscribe.ai/terms-of-use", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Terms of Use
                  </button>
                  <button
                    className="w-full px-4 py-2 text-left text-base text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowHelpDropdown(false);
                       window.open("https://virtualscribe.ai/privacy-policy", "_blank", "noopener,noreferrer");
                    }}
                  >
                    Privacy Policy
                  </button>
                  <button
                   className="w-full px-4 py-2 text-left text-base text-gray-700 hover:bg-gray-50"
                   onClick={() => {
                    setShowHelpDropdown(false);
                    if (onRestartTutorial) onRestartTutorial();
                  }}
                  >
                    Product Tour
                  </button>
                </div>
              )}
            </div> */}
          </div>
        )}
      </div>

      {/* Row 2: Patient Details - Show for all users when patient info is filled */}
      {isPatientDetailsFilled && (
        <>
          <div className="lg:hidden flex justify-center bg-[#f5f7fc] border-b border-slate-200">
            <button
              onClick={() =>
                setShowMobilePatientDetails(!showMobilePatientDetails)
              }
              className="flex items-center gap-1 py-2 text-sm text-slate-600"
            >
              {showMobilePatientDetails ? (
                <>
                  <ChevronUp size={16} /> Hide Details
                </>
              ) : (
                <>
                  <ChevronDown size={16} /> Show Details
                </>
              )}
            </button>
          </div>
          <div
            className={`bg-[#f5f7fc] border-b border-slate-200 overflow-hidden transition-all duration-300 ${showMobilePatientDetails ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0 lg:max-h-[1000px] lg:opacity-100"}`}
          >
            <div className="px-3 sm:px-4 py-2.5 flex flex-col lg:flex-row items-start lg:items-center justify-between text-base text-slate-700 gap-1.5 lg:gap-0">
              <div
                ref={infoCardsRef}
                className="grid items-stretch gap-1.5 w-full max-w-[68rem] min-w-0"
                style={{
                  gridTemplateColumns: INFO_CARDS_GRID_COLUMNS[infoCardsLayout],
                }}
              >
                {/* Patient card */}
                <div
                  className={INFO_CARD_BASE}
                  style={getInfoCardStyle(infoCardsLayout, 0)}
                >
                  <User size={14} className={cardIconClass} />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>Patient</span>
                    <div className="flex items-center gap-x-1.5 flex-wrap min-w-0">
                      <span
                        className={`${INFO_CARD_VALUE} truncate max-w-full`}
                        title={patientContext.name || undefined}
                      >
                        {patientContext.name || "----"}
                      </span>
                      <span
                        ref={pronounButtonRef}
                        className="text-xs text-blue-600 cursor-pointer hover:text-blue-800 hover:underline inline-flex items-center gap-0.5 font-medium whitespace-nowrap py-0.5"
                        onClick={() => {
                          if (pronounButtonRef.current) {
                            // Anchor the popover under the link, clamped so it
                            // always stays fully inside the screen (phones too).
                            const rect =
                              pronounButtonRef.current.getBoundingClientRect();
                            const PAD = 8;
                            const width = Math.min(
                              288,
                              window.innerWidth - PAD * 2,
                            );
                            const top = rect.bottom + 8;
                            setPronounPosition({
                              top,
                              left: Math.max(
                                PAD,
                                Math.min(
                                  rect.left,
                                  window.innerWidth - width - PAD,
                                ),
                              ),
                              width,
                              maxHeight: Math.max(
                                180,
                                window.innerHeight - top - PAD,
                              ),
                            });
                          }
                          setShowPronounCard((prev) => !prev);
                        }}
                      >
                        {currentPronounInfo
                          ? currentPronounInfo
                          : "+ add pronouns"}
                        <ChevronDown
                          size={12}
                          className={`transition-transform ${showPronounCard ? "rotate-180" : ""}`}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                {/* Visit Type */}
                <div
                  className={INFO_CARD_BASE}
                  style={getInfoCardStyle(infoCardsLayout, 1)}
                >
                  <Activity size={14} className={cardIconClass} />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>Type</span>
                    <span
                      className={`${INFO_CARD_VALUE} whitespace-nowrap truncate`}
                      title={formatVisitTypeLabel(patientContext.visitType)}
                    >
                      {formatVisitTypeLabel(patientContext.visitType)}
                    </span>
                  </div>
                </div>
                {/* HPI Level — editable dropdown before generation, read-only after */}
                <div
                  className={INFO_CARD_BASE}
                  style={getInfoCardStyle(infoCardsLayout, 2)}
                >
                  <FileText size={14} className={cardIconClass} />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>HPI Level</span>
                    {soapNote?.metadata?.hpiDepthLevel != null ? (
                      canEditPatientInfo && onRegenerateHpi ? (
                        <Select
                          value={String(soapNote.metadata.hpiDepthLevel)}
                          disabled={hpiRegenerating}
                          onValueChange={(v) =>
                            onRegenerateHpi(parseInt(v, 10))
                          }
                        >
                          <SelectTrigger
                            title="Change the HPI depth for this chart"
                            className={`h-auto w-auto gap-1 border-0 bg-transparent p-0 shadow-none ring-offset-0 focus:ring-0 focus:ring-offset-0 [&>svg]:hidden ${INFO_CARD_VALUE}`}
                          >
                            <SelectValue />
                            <span className="ml-0.5 flex items-center">
                              {hpiRegenerating ? (
                                <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
                              ) : (
                                <Edit size={14} className="text-blue-400" />
                              )}
                            </span>
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {[3, 2, 1].map((lvl) => {
                              const variants =
                                soapNote.metadata.hpiVariants || {};
                              const isCurrent =
                                Number(soapNote.metadata.hpiDepthLevel) === lvl;
                              const isGenerated =
                                isCurrent ||
                                variants[lvl] != null ||
                                variants[String(lvl)] != null;
                              const label =
                                lvl === 1
                                  ? "Concise"
                                  : lvl === 2
                                    ? "Balanced"
                                    : "Detailed";
                              return (
                                <SelectItem
                                  key={lvl}
                                  value={String(lvl)}
                                  className="cursor-pointer"
                                >
                                  <span className="flex items-center gap-2">
                                    {label}
                                    {isGenerated && !isCurrent && (
                                      <span className="text-xs font-medium uppercase tracking-wide text-emerald-600">
                                        saved
                                      </span>
                                    )}
                                  </span>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span
                          className={`${INFO_CARD_VALUE} whitespace-nowrap`}
                        >
                          {(() => {
                            const level = soapNote.metadata.hpiDepthLevel;
                            if (level === 1) return "Concise";
                            if (level === 2) return "Balanced";
                            if (level === 3) return "Detailed";
                            return "—";
                          })()}
                        </span>
                      )
                    ) : canEditPatientInfo &&
                      onHpiLevelChange &&
                      !isProPlusLocked ? (
                      <Select
                        value={String(hpiLevel)}
                        onValueChange={(v) => onHpiLevelChange(parseInt(v, 10))}
                      >
                        <SelectTrigger
                          title="Choose the HPI detail level for this chart"
                          className={`h-auto w-auto gap-1 border-0 bg-transparent p-0 shadow-none ring-offset-0 focus:ring-0 focus:ring-offset-0 [&>svg]:text-blue-400 ${INFO_CARD_VALUE}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="1" className="cursor-pointer">
                            Concise
                          </SelectItem>
                          <SelectItem value="2" className="cursor-pointer">
                            Balanced
                          </SelectItem>
                          <SelectItem value="3" className="cursor-pointer">
                            Detailed
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span
                        className={`${INFO_CARD_VALUE} whitespace-nowrap`}
                        title={
                          isProPlusLocked
                            ? "HPI Concise & Detailed modes are available on Professional and Elite"
                            : undefined
                        }
                      >
                        {hpiLevel === 1
                          ? "Concise"
                          : hpiLevel === 2
                            ? "Balanced"
                            : "Detailed"}
                      </span>
                    )}
                  </div>
                </div>
                {/* Visit Date (editable). Editing happens in a floating popover
                    (see edit popup at the bottom) so this card never changes
                    size and the other cards never move. */}
                <div
                  ref={visitDateCardRef}
                  className={`${INFO_CARD_BASE} relative ${editingField === "visitDate" ? "ring-2 ring-blue-300" : ""}`}
                  style={getInfoCardStyle(infoCardsLayout, 3)}
                >
                  <Clock size={14} className={cardIconClass} />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>Visit Date</span>
                    <div className="flex items-center gap-1">
                      <span className={`${INFO_CARD_VALUE} whitespace-nowrap`}>
                        {patientContext.dateOfService
                          ? formatDate(patientContext.dateOfService)
                          : "—"}
                      </span>
                      {canEditPatientInfo && (
                        <Edit
                          size={14}
                          className="shrink-0 text-blue-400 cursor-pointer hover:text-blue-600"
                          onClick={startEditVisitDate}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* Age (editable via popover) */}
                <div
                  ref={ageCardRef}
                  className={`${INFO_CARD_BASE} relative ${editingField === "age" ? "ring-2 ring-blue-300" : ""}`}
                  style={getInfoCardStyle(infoCardsLayout, 4)}
                >
                  <Calendar size={14} className={cardIconClass} />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>Age</span>
                    <div className="flex items-center gap-1">
                      <span className={INFO_CARD_VALUE}>
                        {patientContext.age || "—"}
                      </span>
                      {canEditPatientInfo && (
                        <Edit
                          size={14}
                          className="shrink-0 text-blue-400 cursor-pointer hover:text-blue-600"
                          onClick={startEditAge}
                        />
                      )}
                    </div>
                  </div>
                </div>
                {/* Gender (editable via popover) */}
                <div
                  ref={genderCardRef}
                  className={`${INFO_CARD_BASE} relative ${editingField === "gender" ? "ring-2 ring-blue-300" : ""}`}
                  style={getInfoCardStyle(infoCardsLayout, 5)}
                >
                  <User size={14} className={cardIconClass} />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>Gender</span>
                    <div className="flex items-center gap-1 min-w-0">
                      <span
                        className={`${INFO_CARD_VALUE} truncate`}
                        title={patientContext.gender || undefined}
                      >
                        {patientContext.gender || "—"}
                      </span>
                      {canEditPatientInfo && (
                        <Edit
                          size={14}
                          className="shrink-0 text-blue-400 cursor-pointer hover:text-blue-600"
                          onClick={startEditGender}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div
                  style={getInfoCardStyle(infoCardsLayout, 6)}
                  className={`${INFO_CARD_BASE} cursor-pointer ${
                    !isRecording ? "bg-[#eafaf6] border-[#d3f2e8]" : ""
                  }`}
                  onClick={onShowTranscriptionInSoapTab}
                >
                  <AudioLines
                    size={14}
                    className={`shrink-0 ${
                      cardIconClass === "hidden"
                        ? "hidden"
                        : isRecording
                          ? "text-red-500 animate-pulse"
                          : "text-teal-500"
                    }`}
                  />
                  <div className="flex flex-col gap-0.5 leading-tight min-w-0">
                    <span className={INFO_CARD_LABEL}>Transcription</span>
                    <span
                      className={`text-sm font-bold whitespace-nowrap ${
                        isRecording
                          ? isParked || isPaused
                            ? "text-amber-600"
                            : "text-red-500"
                          : "text-teal-600"
                      }`}
                    >
                      {isRecording
                        ? isResumingCapture
                          ? "Resuming…"
                          : isParked
                            ? "Parked"
                            : isPaused
                              ? "Paused"
                              : "Recording..."
                        : "Ready"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit popover for Visit Date / Age / Gender (keeps cards fixed in place) */}
      {editingField && editPopupPos && (
        <div
          ref={
            editingField === "age"
              ? ageEditRef
              : editingField === "gender"
                ? genderEditRef
                : visitDateEditRef
          }
          className={`fixed z-[10000] box-border flex gap-1.5 bg-white rounded-xl shadow-xl border border-blue-300 ring-2 ring-blue-100 p-2 ${
            editingField === "visitDate" ? "flex-col items-stretch" : "items-center"
          }`}
          style={{
            top: editPopupPos.top,
            left: editPopupPos.left,
            width:
              editingField === "visitDate"
                ? editPopupPos.width
                : undefined,
            maxWidth: "calc(100vw - 16px)",
            maxHeight: `calc(100vh - ${Math.max(EDIT_POPUP_MARGIN, editPopupPos.top) + EDIT_POPUP_MARGIN}px)`,
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          {editingField === "visitDate" && (
            <>
            {/* Type the date freely (MM/DD/YYYY). Selecting a calendar day saves it. */}
              <div className="flex min-w-0 w-full items-center gap-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  value={tempVisitDateText}
                  onChange={(e) => {
                    const text = formatTypedDate(e.target.value, tempVisitDateText);
                    setTempVisitDateText(text);
                    const iso = displayToIsoDate(text);
                    if (iso && iso <= visitDateMaxIso) {
                      setTempVisitDate(iso);
                      setVisitCalendarMonth(new Date(`${iso}T00:00:00`));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveVisitDate();
                    if (e.key === "Escape") cancelEdit();
                  }}
                  placeholder="MM/DD/YYYY"
                  aria-label="Visit date"
                  className="min-w-0 w-full px-2 py-1 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className="w-full min-w-0 rounded-lg border border-slate-200 bg-white p-2 sm:p-2.5">
                  <div className="mb-2 flex items-center gap-1 min-w-0">
                    <button
                      type="button"
                      onClick={() =>
                        setVisitCalendarMonth((date) =>
                          clampCalendarMonth(
                            new Date(date.getFullYear(), date.getMonth() - 1, 1),
                            visitDateMaxIso,
                          ),
                        )
                      }
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                      aria-label="Previous month"
                      disabled={
                        visitCalendarMonth.getFullYear() === VISIT_CALENDAR_MIN_YEAR &&
                        visitCalendarMonth.getMonth() === 0
                      }
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <div className="flex min-w-0 flex-1 items-center gap-1">
                      <div className="relative min-w-0 flex-1">
                        <select
                          aria-label="Visit date month"
                          value={visitCalendarMonth.getMonth()}
                          onChange={(event) =>
                            setVisitCalendarMonth((date) =>
                              clampCalendarMonth(
                                new Date(
                                  date.getFullYear(),
                                  Number(event.target.value),
                                  1,
                                ),
                                visitDateMaxIso,
                              ),
                            )
                          }
                          className="h-9 w-full min-w-0 appearance-none truncate rounded-md border border-slate-200 bg-white px-1.5 pr-6 text-sm font-medium"
                        >
                          {VISIT_CALENDAR_MONTHS.map((month, index) => {
                            const year = visitCalendarMonth.getFullYear();
                            const afterMax =
                              visitDateMaxParts &&
                              (year > visitDateMaxParts.year ||
                                (year === visitDateMaxParts.year &&
                                  index > visitDateMaxParts.month));
                            return (
                              <option
                                key={month}
                                value={index}
                                disabled={afterMax}
                                className="text-sm"
                              >
                                {month}
                              </option>
                            );
                          })}
                        </select>
                        <ChevronDown
                          size={16}
                          className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500"
                        />
                      </div>
                      <div className="relative min-w-0 flex-[0.85]">
                        <button
                          ref={visitYearButtonRef}
                          type="button"
                          aria-label="Visit date year"
                          aria-haspopup="listbox"
                          aria-expanded={showVisitYearList}
                          onClick={() =>
                            setShowVisitYearList((isOpen) => !isOpen)
                          }
                          className="flex h-9 w-full min-w-0 items-center justify-between rounded-md border border-slate-200 bg-white px-1.5 text-sm font-medium"
                        >
                          {visitCalendarMonth.getFullYear()}
                          <ChevronDown size={16} className="shrink-0 text-slate-500" />
                        </button>
                        {showVisitYearList && (
                          <div
                            ref={visitYearListRef}
                            role="listbox"
                            aria-label="Visit date year"
                            className={`absolute left-0 right-0 z-20 overflow-y-auto overscroll-contain rounded-md border border-slate-200 bg-white py-1 shadow-lg ${
                              visitYearListPlacement.openUp
                                ? "bottom-full mb-1"
                                : "top-full mt-1"
                            }`}
                            style={{ maxHeight: visitYearListPlacement.maxHeight }}
                          >
                            {visitDateEditYears.map((year) => (
                              <button
                                key={year}
                                ref={
                                  year === visitCalendarMonth.getFullYear()
                                    ? selectedVisitYearRef
                                    : null
                                }
                                type="button"
                                role="option"
                                aria-selected={
                                  year === visitCalendarMonth.getFullYear()
                                }
                                onClick={() => {
                                  setVisitCalendarMonth((date) =>
                                    clampCalendarMonth(
                                      new Date(year, date.getMonth(), 1),
                                      visitDateMaxIso,
                                    ),
                                  );
                                  setShowVisitYearList(false);
                                }}
                                className={`block h-6 w-full px-2 text-left text-sm hover:bg-blue-50 ${
                                  year === visitCalendarMonth.getFullYear()
                                    ? "bg-blue-50 text-blue-700"
                                    : "text-slate-700"
                                }`}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setVisitCalendarMonth((date) =>
                          clampCalendarMonth(
                            new Date(date.getFullYear(), date.getMonth() + 1, 1),
                            visitDateMaxIso,
                          ),
                        )
                      }
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                      aria-label="Next month"
                      disabled={
                        !!visitDateMaxParts &&
                        (visitCalendarMonth.getFullYear() > visitDateMaxParts.year ||
                          (visitCalendarMonth.getFullYear() === visitDateMaxParts.year &&
                            visitCalendarMonth.getMonth() >= visitDateMaxParts.month))
                      }
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 text-center text-[11px] sm:text-xs font-medium text-slate-500">
                    {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                      <span key={day} className="py-1">{day}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-0.5 text-center text-sm">
                    {getVisitCalendarDays(visitCalendarMonth).map((day) => {
                      const isCurrentMonth =
                        day.getMonth() === visitCalendarMonth.getMonth();
                      const iso = formatIsoDate(day);
                      const isSelected = iso === tempVisitDate;
                      const isOutOfRange = iso > visitDateMaxIso;
                      return (
                        <button
                          key={iso}
                          type="button"
                          disabled={isOutOfRange}
                          onClick={() => selectVisitDate(iso)}
                          className={`aspect-square w-full min-h-[1.75rem] rounded-md ${
                            isOutOfRange
                              ? "cursor-not-allowed text-slate-300"
                              : isSelected
                                ? "bg-blue-600 font-semibold text-white"
                                : isCurrentMonth
                                  ? "text-slate-800 hover:bg-blue-50"
                                  : "text-slate-400 hover:bg-blue-50"
                          }`}
                          aria-label={day.toLocaleDateString("en", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                          })}
                          aria-pressed={isSelected}
                          aria-disabled={isOutOfRange}
                        >
                          {day.getDate()}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => selectVisitDate(visitDateTodayIso)}
                    className="mt-2 w-full rounded-md py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Today
                  </button>
                </div>
            </>
          )}
          {editingField === "age" && (
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              value={tempAge}
              onChange={(e) => {
                // Strip anything that isn't a digit, then cap at 2 characters.
                const digitsOnly = e.target.value
                  .replace(/\D/g, "")
                  .slice(0, 2);
                setTempAge(digitsOnly);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveAge();
                if (e.key === "Escape") cancelEdit();
              }}
              placeholder="Age"
              className="w-16 px-2 py-1 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          )}
          {editingField === "gender" && (
            <select
              ref={(el) => {
                if (!el) return;
                // Native select — open the list immediately, no second click needed.
                // rAF lets the element finish mounting before we ask the browser to open it.
                requestAnimationFrame(() => {
                  try {
                    el.showPicker?.();
                  } catch {
                    /* some browsers require a stricter user gesture; ignore */
                  }
                });
              }}
              value={tempGender}
              onChange={(e) => {
                // Simple select -> save and close right away (no tick / cross needed)
                setTempGender(e.target.value);
                saveGender(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") cancelEdit();
              }}
              className="w-36 px-2 py-1 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            >
              {/* Placeholder is not selectable, so an existing gender can't be accidentally cleared */}
              <option value="" disabled hidden>Select gender</option>
              {/* Keep a free-text / custom gender (entered in the patient form) selectable */}
              {tempGender && !["Male", "Female", "Other"].includes(tempGender) && (
                <option value={tempGender}>{tempGender}</option>
              )}
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          )}
        </div>
      )}

      {/* Pronoun Card Popup */}
      {showPronounCard && (
        <div
          ref={pronounCardRef}
          className="fixed z-[9999] overflow-y-auto overflow-x-hidden bg-white rounded-2xl shadow-xl border border-gray-200"
          style={{
            top: pronounPosition.top,
            left: pronounPosition.left,
            width: pronounPosition.width,
            maxHeight: pronounPosition.maxHeight,
          }}
        >
          <SoapNotePronounBar
            soapNote={soapNote}
            patientContext={patientContext}
            aiService={aiService}
            onUpdateSoapNote={async (updatedNote) => {
              await onUpdateSoapNote(updatedNote);
              setShowPronounCard(false);
            }}
            onStartProcessing={onStartProcessing}
            onEndProcessing={onEndProcessing}
            onCancel={() => setShowPronounCard(false)}
          />
        </div>
      )}

      {/* VBot Subscription Modal */}
      <VBotSubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onSubscribe={(planType, billingPeriod, seats) => {
          console.log("Selected plan:", { planType, billingPeriod, seats });
          setShowSubscriptionModal(false);
        }}
        onContactSales={goToTalkToSales}
      />
    </div>
  );
}